import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import Box from 'fe-design-base/atoms/Box';
import { useIsEmbedded } from 'hooks/useIsEmbedded';
import { List } from 'immutable';
import PropTypes from 'prop-types';

import { cxHelpers } from 'util/className';
import { moment } from 'util/dateTime';

import EditHistory from '../EditHistory';
import NoteSection from '../NoteSection';
import PhotosSection from '../PhotosSection';

import Header from './Header';
import TimebreakSection from './TimebreakSection';
import TimecardSection from './TimecardSection';
import TipsSection from './TipsSection';

const { cx } = cxHelpers('TimecardModalReadMode');

const HEADER_SECTION_HEIGHT = 182;
const EMBEDDED_HEADER_HEIGHT_ADJUSTMENT = 310;

const ReadMode = ({
  addTimebreak,
  breakPenaltiesEnabled,
  canManageTimecards,
  closeModal,
  dismissMissedBreaks,
  editMode,
  expandBreaks,
  timesheetApprovalEnabled,
  hasTimecardLockPermission,
  hideTipsSection,
  isLandscape,
  isLockDisabled,
  isMobile,
  onChangeShift,
  onUpdateLockedStatus,
  paywallVersion,
  shift,
  submitChanges,
  setChangesMade,
}) => {
  const isEmbedded = useIsEmbedded();

  return (
    <Box
      className={cx()}
      h={isMobile || isEmbedded ? '100%' : null}
      overflow="hidden"
      bradiusm
      bcolor="mono300"
      bw={1}
      w={isMobile ? '100%' : 640}
      b={isEmbedded ? 0 : null}
    >
      <Box bgcolor="mono0" bcolor="mono300" bbw={1}>
        <Header
          closeModal={closeModal}
          canManageTimecards={canManageTimecards}
          date={moment(shift.get('date'))}
          editMode={editMode}
          employeeId={shift.get('employee_id')}
          employeeName={shift.get('employee_name')}
          employeeFirstName={shift.get('employee_first_name')}
          employeePhone={shift.get('employee_phone')}
          hasTimecardLockPermission={hasTimecardLockPermission}
          hideLockIcon={!shift.get('actual_start_at')}
          isLockDisabled={isLockDisabled}
          isMobile={isMobile}
          locationName={shift.get('location_name')}
          lockedMessage={shift.get('locked_message')}
          onUpdateLockedStatus={onUpdateLockedStatus}
          paywallVersion={paywallVersion}
          roleName={shift.get('role_name')}
          timesheetApprovalEnabled={timesheetApprovalEnabled}
        />
      </Box>
      <Box
        maxh={
          isEmbedded
            ? `calc(90vh - ${EMBEDDED_HEADER_HEIGHT_ADJUSTMENT}px`
            : `calc(90vh - ${isLandscape ? 300 : HEADER_SECTION_HEIGHT}px`
        }
        overflowY="auto"
      >
        <TimecardSection
          addTimebreak={addTimebreak}
          canManageTimecards={canManageTimecards}
          onChangeShift={onChangeShift}
          shift={shift}
          setChangesMade={setChangesMade}
        />
        {shift.get('timebreaks') && (
          <TimebreakSection
            addTimebreak={addTimebreak}
            breakPenaltiesEnabled={breakPenaltiesEnabled}
            dismissMissedBreaks={dismissMissedBreaks}
            expandBreaks={expandBreaks}
            shift={shift}
            submitChanges={submitChanges}
            setChangesMade={setChangesMade}
          />
        )}

        {!hideTipsSection && (
          <TipsSection
            cashTips={shift.get('cash_tips')}
            creditTips={shift.get('credit_tips')}
            tipOut={shift.get('tip_out')}
            tipOutUpdatedAt={shift.get('tip_out_updated_at')}
            cashTipOutEligible={shift.get('cash_tip_out_eligible')}
          />
        )}

        {canManageTimecards && (
          <NoteSection
            employeeName={shift.get('short_name')}
            feedback={shift.get('feedback')}
            notes={shift.get('notes') || List()}
            shiftId={shift.get('id')}
          />
        )}

        <PhotosSection
          isMobile={isMobile}
          photos={shift.get('photos') || List()}
        />

        <EditHistory events={shift.get('edit_history_events') || List()} />
      </Box>
    </Box>
  );
};

ReadMode.propTypes = {
  addTimebreak: PropTypes.func.isRequired,
  breakPenaltiesEnabled: PropTypes.bool,
  canManageTimecards: PropTypes.bool.isRequired,
  dismissMissedBreaks: PropTypes.func.isRequired,
  editMode: PropTypes.bool.isRequired,
  isLockDisabled: PropTypes.bool.isRequired,
  onUpdateLockedStatus: PropTypes.func.isRequired,
  paywallVersion: PropTypes.string,
  shift: ImmutablePropTypes.map.isRequired,
  setChangesMade: PropTypes.func.isRequired,
};

export default ReadMode;
